/**
 * Event utilities
 * Bot detection and event ID generation
 */

import type { WindowAnalyzify } from '../types/window-analyzify';

/**
 * Bot patterns to exclude from TikTok tracking
 * TikTok Pixel can be affected by bot traffic polluting analytics data
 * Comprehensive list matching Shopify extension patterns (~50 patterns)
 */
export const BOT_PATTERNS = [
  // Development tools
  'PostmanRuntime', 'Postman', 'insomnia', 'Thunder Client',
  'python-requests', 'curl', 'wget', 'httpie',
  // Generic bot patterns
  'bot', 'spider', 'crawl', 'scraper', 'headless',
  // Search engine bots
  'googlebot', 'baiduspider', 'yandex', 'bingbot', 'duckduckbot',
  'sogou', 'exabot', 'ia_archiver', 'ahrefsbot', 'semrushbot',
  'dotbot', 'rogerbot', 'seznambot', 'mj12bot',
  // Social media bots
  'facebookexternalhit', 'facebot', 'twitterbot', 'linkedinbot',
  'pinterest', 'pinterestbot', 'slackbot', 'vkShare', 'Discordbot',
  'telegrambot', 'WhatsApp', 'snapchat',
  // Preview/embed bots
  'embedly', 'quora link preview', 'showyoubot', 'outbrain',
  'flipboard', 'tumblr', 'bitlybot', 'SkypeUriPreview', 'nuzzel',
  'Bitrix link preview', 'XING-contenttabreceiver',
  // Validation/monitoring
  'W3C_Validator', 'validator', 'lighthouse', 'gtmetrix', 'pagespeed',
  'pingdom', 'uptimerobot', 'StatusCake',
  // Other common bots
  'redditbot', 'Applebot', 'Qwantify', 'petalbot', 'coccocbot',
  'amazonbot', 'bytespider', 'dataforseo', 'gptbot', 'claudebot',
];

/**
 * Check if current user agent is a bot
 * Used to prevent bot traffic from polluting analytics
 */
export function isBot(): boolean {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return BOT_PATTERNS.some(pattern => userAgent.includes(pattern.toLowerCase()));
}

/**
 * Generate unique event ID using CDN's window.analyzify.generateEventId()
 * Falls back to local sh-prefixed UUID if CDN not available (race condition, load failure)
 */
export function generateEventId(): string {
  const win = window as any;
  if (typeof win.analyzify?.generateEventId === 'function') {
    return win.analyzify.generateEventId('sh', '');
  }
  // Fallback: generate sh-prefixed ID locally (matches CDN format, no dashes)
  const segments = [8, 4, 4, 4, 12];
  const hex = '0123456789abcdef';
  const parts = segments.map((len) => {
    let s = '';
    for (let i = 0; i < len; i++) s += hex[Math.floor(Math.random() * 16)];
    return s;
  });
  return `sh${parts.join('')}`;
}

/**
 * Shared GTM dataLayer shop/market fields.
 * Returns the shop_locale/shop_country/market_* block appended to every ee_*
 * dataLayer push across the event handlers.
 */
export function gtmMarketFields(analyzify: WindowAnalyzify): Record<string, any> {
  const shop = analyzify.shop;
  const market = analyzify.market;
  return {
    shop_locale: shop?.locale || null,
    shop_country: shop?.country || null,
    market_country: market?.country || null,
    market_language: market?.language || null,
    market_handle: market?.handle || null,
  };
}
